<template>
  <div class="site-header-container">
    <header
      class="site-header"
      :class="headerClasses"
    >
      <div class="container site-header__container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="isMobile && mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <navigation
            v-if="!isMobile"
            :desktop="desktop"
          />
        </div>

        <!-- Logo -->
        <component
          :is="titleElement"
          class="site-header__center"
        >
          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
          >
            <span
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />

            <brand-logo />
          </a>
        </component>

        <!-- Right -->
        <div class="site-header__right">
          <multi-store-trigger />

          <div v-if="liquid.enableLanguageSwitcher && !isMobile">
            <language-switcher />
          </div>

          <a
            class="site-header__account icon-button"
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
            <span
              v-if="customerGreeting"
              class="customer-name text-body-xs-regular"
              v-text="customerGreeting"
            />

          </a>

          <label
            v-if="showSearchIcon"
            role="button"
            tabindex="0"
            for="search-drawer-input"
            class="site-header__search icon-button"
            type="button"
            @keypress.enter.prevent="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @keypress.space.prevent="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @click="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @keyup.enter="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
          >
            <span
              class="visually-hidden"
              v-text="$string('predictive_search.accessibility_label')"
            />
            <icon-search v-if="!isMobile" />
            <icon-search-mobile v-if="isMobile" />
          </label>

          <a
            class="site-header__cart icon-button"
            :href="$variable('routes.cart.url')"
            data-button="true"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              v-if="cart.item_count > 0"
              class="site-header__cart-badge critical-clear text-body-3xs-regular"
            >
              <span v-text="cart.item_count" />
              <span
                class="visually-hidden"
                v-text="$string('cart.accessibility.item_count')"
              />
            </span>
          </a>
        </div>
      </div>

      <div
        v-if="isMobile && mobileExposedSearch"
        class="exposed-search"
      >
        <div
          class="exposed-search__wrapper search-drawer__form"
        >
          <label
            for="exposed-search-input"
            class="visually-hidden"
            v-text="$string('accessibility.search.field_label')"
          />

          <icon-search-mobile v-if="isMobile" />

          <div
            id="exposed-search-input"
            class="search-drawer__input"
            role="button"
            tabindex="0"
            type="search"
            @keypress.enter.prevent="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @keypress.space.prevent="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @click="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
            @keyup.enter="cnvs?.EventBus?.emit('overlay:open-search-drawer', 'left')"
          >
            <span
              class="placeholder"
              v-text="$string('search.exposed_search_placeholder')"
            />
          </div>
        </div>
      </div>
    </header>

    <!-- Menu drawer -->
    <teleport
      v-if="isMobile && mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        :overlay-label="$string('general.site_header.navigation')"
        :show-close="false"
        type="drawer"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobileMenu"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :image="mobile.image"
            :show-search="mobile.showSearch"
            :customer-greeting="customerGreeting"
            :enable-mobile-menu-search="mobile.enableMobileMenuSearch"
            :enable-language-switcher="liquid.enableLanguageSwitcher"
            :slide-out-menu="slideOutMenu"
          />
        </template>
      </overlay>
    </teleport>

    <teleport
      v-if="isMobile"
      to="body"
    >
      <quick-add-to-cart
        :product="product"
        :final-sale="finalSale"
        @form-submitted="handleDynamicYieldOverlay"
      />
    </teleport>

    <teleport
      v-if="!isMobile"
      to="body"
    >
      <quick-add-success
        type="pop-up"
        placement="quickAddSuccess"
      />
    </teleport>

    <size-guide-drawer
      :size-guide-settings="liquid?.sizeGuide"
      :iso-code="liquid?.sizeGuide?.isoCode"
    />

    <teleport
      v-if="isMobile"
      to="body"
    >
      <lookbook-product-list-modal />
    </teleport>

    <teleport to="body">
      <wishlist-add-success-modal />

      <wishlist-product-card-modal
        v-if="cnvs?.template?.name === 'account' && cnvs?.template?.suffix === 'favorites'"
      />
    </teleport>

    <teleport
      v-if="
        isMobile && cnvs?.template?.name === 'account' && cnvs?.template?.suffix === 'favorites'
      "
      to="body"
    >
      <wishlist-add-to-cart-success />
    </teleport>

    <teleport
      v-if="isMobile"
      to="body"
    >
      <overlay
        key="product-form-success-mobile"
        block-class="quick-add-success-modal"
        direction="up"
        mobile-drag-close
        namespace="productFormSuccessMobile"
        :show-close="false"
        show-overlay
        type="shelf-modal"
      >
        <template #body>
          <quick-add-success
            type="inline"
            show-upsell
          />
        </template>
      </overlay>
    </teleport>

    <free-gift v-if="$variable('settings.fgwp.isActive') === 'true'" />

    <product-form-drawer />
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} colors - Background and text colors from section settings.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import Navigation from '../navigation/navigation.vue'

import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'
import IconSearchMobile from '~icons/general/search-mobile.svg'

import FreeGift from '~async/free-gift/free-gift'
import MultiStoreTrigger from '~async/multi-store-trigger/multi-store-trigger'
import LanguageSwitcher from '~global/language-switcher/language-switcher'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconSearch,
    IconSearchMobile,
    IconMenu,
    LanguageSwitcher,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Navigation,
    MultiStoreTrigger,
    Overlay,
    SizeGuideDrawer: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.size-guide-drawer' */
        '~global/size-guide/size-guide-drawer'
      ),
    }),
    QuickAddSuccess: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.quick-add-success' */
        '~async/quick-add-to-cart/quick-add-success'
      ),
    }),
    QuickAddToCart: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.quick-add-to-cart' */
        '~async/quick-add-to-cart/quick-add-to-cart'
      ),
    }),
    LookbookProductListModal: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.lookbook-product-list-modal' */
        '~async/lookbook-images/lookbook-product-list-modal'
      ),
    }),
    FreeGift,
    ProductFormDrawer: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.product-form-drawer' */
        '~global/product-form-drawer/product-form-drawer'
      ),
    }),
    WishlistProductCardModal: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.wishlist-product-card-modal' */
        '~async/customers/favorites/wishlist-product-card-modal'
      ),
    }),
    WishlistAddSuccessModal: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.wishlist-add-success-modal' */
        '~async/customers/favorites/wishlist-add-success-modal'
      ),
    }),
    WishlistAddToCartSuccess: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.wishlist-add-to-cart-success' */
        '~async/customers/favorites/wishlist-add-to-cart-success'
      ),
    }),
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    colors: {
      type: Object,
      default: () => ({
        background: 'transparent',
        text: 'white',
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        dyMobileHandle: null,
        itemCount: 5,
        image: {},
        localization: {
          country_code: 'US',
          currency_symbol: '$',
        },
        showSearch: false,
        enableMobileMenuSearch: true,
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    customerGreeting: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cnvs: window.cnvs,
      mobileExposedSearch: false,
      slideOutMenu: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      dyTests: (state) => state['dynamic-yield'].tests,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      product: (state) => state.sizeGuideDrawer.product,
    }),

    /**
     * Determines and returns the mobile menu handle.
     */
    mobileMenu() {
      if (window?.DY?.dyMobileMenu === true && this.mobile.dyMobileHandle) {
        return this.mobile.dyMobileHandle
      }

      return this.mobile.handle
    },

    /**
     * Compute header classes.
     * @returns {String}
     */
    headerClasses() {
      const classes = []
      const prefix = 'site-header'

      if (this.colors.background) {
        classes.push(`${prefix}--bg-${this.colors.background}`)
      }

      if (this.colors.text) {
        classes.push(`${prefix}--text-${this.colors.text}`)
      }

      if (this.headerState) {
        classes.push(`${prefix}--${this.headerState}`)
      }

      if (this.navOpen) {
        classes.push(`${prefix}--nav-open`)
      }

      if (this.mobileExposedSearch) {
        classes.push('has-exposed-search')
      }

      return classes.join(' ')
    },

    /**
     * Compute if the navigation or menu-drawer overlay is open.
     * @returns {Boolean}
     */
    navOpen() {
      const activeOverlay = this.getActiveOverlay?.namespace
      return activeOverlay === 'navigation' || activeOverlay === 'menu-drawer'
    },

    /**
     * Compute final sale state based on product tags.
     * @returns {Boolean}
     */
    finalSale() {
      const tags = this.product?.tags

      return tags?.some((tag) => {
        return (
          tag.toLowerCase().includes('final sale') ||
          tag.toLowerCase().includes('final_sale') ||
          tag.toLowerCase().includes('finalsale')
        )
      })
    },

    /**
     * Computes if exposed search should be displayed
     */
    showSearchIcon() {
      if (this.mobileExposedSearch && this.isMobile) {
        return false
      }
      return true
    },
  },

  watch: {

    /**
     * Watch DY tests to activate them.
     */
    dyTests: {
      handler() {
        if (this.dyTests.includes('dy-slide-out-mobile-drawer')) {
          this.slideOutMenu = true
        }
      },
      deep: true,
      immediate: true,
    },

    /**
     * Watch is mobile to load stylesheet.
     */
    isMobile: {
      handler(newIsMobile) {
        if (newIsMobile) {
          if (this.mobile?.handle) {
            this.loadStylesheet('menu-drawer')
          }
        }
      },
      immediate: true,
    },
  },

  mounted() {
    this.mobileExposedSearch = this.liquid.enableExposedSearch && (
      window.cnvs.template.name === 'collection' ||
      window.cnvs.template.name === 'index'
    )
    // set toggle value into cnvs variable based on markets
    window.cnvs = window.cnvs || {}
    window.cnvs.settings.product.promoMsgToggle = this.liquid.enablePromoMsg

    if (cnvs.settings.wishi.page === cnvs.page.handle) {
      const isRedirected = localStorage.getItem('isRedirected')
      if (!isRedirected) {
        cnvs.LocalStorage('returnUrl', window.location.href)
      }

      if (isRedirected) {
        localStorage.removeItem('isRedirected')
      }
    }

    if (this.isMobile && window.cnvs?.store?.permanentDomain === '//veronica-beard-canada.myshopify.com/') {
      const browserLang = window.navigator.language
      const manuallySet = localStorage.getItem('userSelectedLocale')
      const alreadyRedirected = sessionStorage.getItem('browserLangRedirectionDone')

      if (!manuallySet && !alreadyRedirected) {
        const prefersFrench = browserLang.startsWith('fr')
        const prefersEnglish = browserLang.startsWith('en')

        const isCurrentlyFrench = window.cnvs?.request.locale.shop_locale.locale === 'fr'
        const isCurrentlyEnglish = window.cnvs?.request.locale.shop_locale.locale === 'en'
        cnvs.SessionStorage('browserLangRedirectionDone', 'true')

        if (prefersFrench && !isCurrentlyFrench) {
          if (!window.location.pathname.startsWith('/fr')) {
            window.location.href = `/fr${window.location.pathname}`
          }
        }

        if (prefersEnglish && !isCurrentlyEnglish) {
          if (window.location.pathname.startsWith('/fr')) {
            window.location.href = window.location.pathname.replace(/^\/fr/, '')
          }
        }
      }
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      loadStylesheet: 'components/loadStylesheet',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer-overlay')
      ) {
        return
      }

      event.preventDefault()

      window.cnvs?.EventBus?.emit('overlay:open-cart-drawer')
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Handle dynamic yield recommendations.
     */
    handleDynamicYieldOverlay() {
      cnvs.EventBus.emit('dy:bis')
    },
  },
}
</script>
